import { Operation } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';

/**
 * OPT-IN retry, for the handful of documents whose failure is unrecoverable for the rest of the
 * session. A blanket retry would replay non-idempotent mutations (optimization budget/target,
 * member removal), so an operation asks for it by spreading `RETRYABLE_CONTEXT`.
 */
export const RETRYABLE_CONTEXT = { retryable: true } as const;

/** Bounded: ~4 network round trips, then the error surfaces and the caller decides. */
export const MAX_ATTEMPTS = 4;

const INITIAL_DELAY_MS = 300;

function isMutation(operation: Operation): boolean {
  return operation.query.definitions.some(
    (definition) => definition.kind === 'OperationDefinition' && definition.operation === 'mutation',
  );
}

/**
 * `RetryLink` only ever sees NETWORK errors — a GraphQL error in a 200 response never reaches
 * here, which is what we want: an undeployed field fails validation identically on every attempt.
 * A 4xx is deterministic for the same reason (and 401 is the login redirect), so only 5xx and a
 * transport failure with no status are worth a second trip.
 */
export function shouldRetryOperation(error: unknown, operation: Operation): boolean {
  if (operation.getContext().retryable !== true) return false;
  if (isMutation(operation)) return false;

  const statusCode = (error as { statusCode?: number } | null)?.statusCode;
  if (typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500) return false;

  return true;
}

/** `initialDelayMs` is overridden only by tests, which assert the policy, not the backoff. */
export function createRetryLink(initialDelayMs: number = INITIAL_DELAY_MS): RetryLink {
  return new RetryLink({
    delay: { initial: initialDelayMs, jitter: true },
    attempts: { max: MAX_ATTEMPTS, retryIf: shouldRetryOperation },
  });
}
